import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { ContactBoxInCartData } from './contact-box-in-cart.component.converter'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { CA, US } from '../../shared/constants/countries'
import { map, shareReplay, tap } from 'rxjs/operators'
import { MbConstants } from '../../../../shared/mb-constants'
import { CaasIdType } from '../../../enums/caas-id-types'
import { MbCaasService } from '../../../services/mb-caas/mb-caas.service'
import { CountryService } from '../../../../site-context/facade/country.service'
import { ContactBoxInCartContactData } from './contact-box-in-cart.module'
import { CmsComponentUtils } from '../../utils/cms-component-utils'
import { ActiveCartService } from '@spartacus/cart/base/core'
import { ComponentState } from '../../types'
import { CmsBaseComponent } from '../../shared/cms-base-component/cms-base.component'

@Component({
  selector: 'mb-contact-box-in-cart',
  styleUrls: ['./contact-box-in-cart.component.scss'],
  templateUrl: './contact-box-in-cart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactBoxInCartComponent extends CmsBaseComponent<ContactBoxInCartData> implements OnInit {

  contactData$ = new BehaviorSubject<ContactBoxInCartContactData | undefined>(undefined)
  isVisible$ = new BehaviorSubject<boolean>(true)

  isUsOrCanada$ = this.countryService.getActive().pipe(
    map(country => country === US || country === CA))

  hasPriceOnRequestItems$ = this.cartService.getEntries().pipe(
    map(entries => entries.some(entry => entry?.basePrice?.value === 0)))

  showRequestEmail$ = combineLatest([this.isUsOrCanada$, this.hasPriceOnRequestItems$, this.contactData$]).pipe(
    map(data => data.every(e => e) && (data?.[2]?.ordersRequestEmail?.trim()?.length ?? 0) > 0))

  state$ = combineLatest([
    this.baseState$,
    this.isVisible$,
    this.contactData$,
    this.isUsOrCanada$,
    this.hasPriceOnRequestItems$,
    this.showRequestEmail$
  ]).pipe(
    map(([baseState, isVisible, contactData, isUsOrCanada, hasPriceOnRequestItems, showRequestEmail]) => ({
      ...baseState,
      isVisible,
      contactData,
      isUsOrCanada,
      hasPriceOnRequestItems,
      showRequestEmail
    } as ContactBoxInCartState)))

  constructor (private cartService: ActiveCartService,
               private caasService: MbCaasService,
               private countryService: CountryService) {
    super()
  }

  ngOnInit () {
    super.ngOnInit()
    this.loadData()
  }

  private loadData (): void {
    const keys = [
      { key: 'page.children.children.formData', operator: 1 },
      { key: 'page.children.children.name', operator: 1 },
      { key: 'locale.country', operator: 1 }
    ]
    combineLatest([this.caasService.getByIds([MbConstants.CONTACT_BOX_IN_CART], CaasIdType.SEOURL, keys), this.baseState$])
      .pipe(
        shareReplay({ refCount: true, bufferSize: 1 }),
        tap(([res, state]) => this.setVisibility(res?.[0] ?? {}, state.isPreview)),
        map(([res, state]) => this.mapContactBoxData(res?.[0] ?? {})))
      .subscribe(contactData => this.contactData$.next(contactData))
  }

  private setVisibility (contactBoxData: any, isPreview: boolean): void {
    const data = contactBoxData?.children?.[1]?.children?.[0]?.formData ?? {}
    const visibility = {
      hide: data.st_hide_component?.value,
      hideBefore: data.st_hide_before_date?.value,
      hideAfter: data.st_hide_after_date?.value
    }
    this.isVisible$.next(CmsComponentUtils.isComponentVisible(visibility, isPreview))
  }

  private mapContactBoxData (data: any): ContactBoxInCartContactData {
    const componentData = data.page?.children?.[1]?.children?.[0] ?? {}
    const contactData = componentData.formData?.['st_contact']?.value?.[0]?.formData ?? {}
    return {
      company: contactData.gc_company?.value ?? '',
      country: contactData.gc_country?.value ?? '',
      street: contactData.gc_street?.value ?? '',
      ordersEmail: contactData.gc_orders_email?.value ?? '',
      ordersPhone: contactData.gc_orders_phone?.value ?? '',
      ordersRequestEmail: contactData.gc_orders_request_email?.value ?? '',
      cityAndZip: contactData.gc_zip_city?.value ?? ''
    }
  }
}

export interface ContactBoxInCartState extends ComponentState<ContactBoxInCartData> {
  contactData: ContactBoxInCartContactData;
  isUsOrCanada: boolean;
  hasPriceOnRequestItems: boolean;
  showRequestEmail: boolean;
}
